import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import BackgroundLayer from "../components/BackgroundLayer";
import BrandLogo from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const APPLE_ERROR_MESSAGES = {
  apple_cancelled: "Apple sign-in was cancelled.",
  apple_state: "Apple sign-in security check failed. Please try again.",
  apple_invalid: "Apple sign-in response was incomplete. Please try again.",
  apple_token: "We couldn't verify your Apple account. Please try again.",
  apple_exchange: "Apple authentication exchange failed. Please try again.",
  apple_no_sub: "Apple didn't return a user identifier. Please try again.",
};

export default function LoginScreen() {
  const [tab, setTab] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Surface Apple OAuth errors after the redirect bounce.
  useEffect(() => {
    const err = params.get("error");
    if (err && APPLE_ERROR_MESSAGES[err]) {
      toast.error(APPLE_ERROR_MESSAGES[err]);
      const next = new URLSearchParams(params);
      next.delete("error");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      if (tab === "login") {
        await login(email, password);
      } else {
        await register(email, password, name || email.split("@")[0]);
      }
      toast.success(tab === "login" ? "Welcome back!" : "Account created");
      navigate("/home", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <BackgroundLayer section="auth" />
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-10">
        <div className="mb-6 fade-up flex justify-center">
          <BrandLogo width={180} />
        </div>
        <div
          className="w-full max-w-md p-7 fade-up stagger-1"
          data-testid="auth-card"
          style={{
            background: "rgba(0, 0, 0, 0.42)",
            border: "1px solid rgba(76, 175, 80, 0.18)",
            borderRadius: 20,
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.35)",
          }}
        >
          {/* Tabs */}
          <div className="flex border-b border-white/10 mb-6">
            {["login", "register"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`auth-tab-${t}`}
                className={`tab-trigger flex-1 py-3 text-sm font-semibold capitalize ${
                  tab === t ? "text-white active" : "text-white/40 hover:text-white/70"
                }`}
              >
                {t === "login" ? "Login" : "Register"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {tab === "register" && (
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-soft">Name</label>
                <input
                  data-testid="auth-name-input"
                  className="glass-input mt-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  required
                />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-soft">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                className="glass-input mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@flora.app"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-soft">Password</label>
              <input
                data-testid="auth-password-input"
                type="password"
                className="glass-input mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
                minLength={6}
              />
            </div>
            {tab === "login" && (
              <div className="text-right">
                <Link to="/forgot-password" className="text-[13px] text-[#4CAF50] hover:underline">
                  Forgot password?
                </Link>
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="btn-primary w-full mt-2"
            >
              {busy ? "Please wait…" : tab === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-[11px] uppercase tracking-wider text-white/40">or</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <button
            onClick={googleLogin}
            data-testid="auth-google"
            className="btn-outline w-full flex items-center justify-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>

          <button
            onClick={() => {
              // Use a same-origin URL (not REACT_APP_BACKEND_URL) so the
              // `apple_state` CSRF cookie is set on the user-facing host
              // (e.g. careforflora.com), matching the domain Apple posts
              // the callback to. Hitting an internal Emergent backend
              // hostname here causes a cross-origin cookie loss + Chrome
              // bounce-tracking warning.
              window.location.href = "/api/auth/apple/start";
            }}
            data-testid="auth-apple"
            className="w-full flex items-center justify-center gap-2 mt-3 rounded-xl py-3 font-semibold transition-all hover:opacity-90"
            style={{
              background: "#000",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
            }}
            aria-label={tab === "login" ? "Sign in with Apple" : "Sign up with Apple"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
              <path d="M16.365 1.43c0 1.14-.46 2.21-1.21 3-.79.84-2.08 1.49-3.13 1.41-.13-1.1.4-2.24 1.13-2.97.78-.78 2.13-1.39 3.21-1.44zM20.5 17.04c-.61 1.36-.91 1.96-1.71 3.16-1.12 1.66-2.69 3.73-4.65 3.75-1.74.02-2.18-1.12-4.54-1.1-2.36.02-2.85 1.12-4.59 1.1-1.96-.02-3.45-1.9-4.57-3.56C-.79 16.6-1.04 11.45 1.27 8.74c1.63-1.91 4.21-3.03 6.65-3.03 2.48 0 4.05 1.36 6.11 1.36 2 0 3.21-1.36 6.07-1.36 2.16 0 4.45 1.18 6.08 3.22-5.34 2.93-4.47 10.56-5.69 8.11z"/>
            </svg>
            Continue with Apple
          </button>

          <p className="text-center text-xs text-white/40 mt-6">
            By continuing you agree to our{" "}
            <Link to="/terms" className="text-[#4CAF50] hover:underline">Terms</Link> &{" "}
            <Link to="/privacy" className="text-[#4CAF50] hover:underline">Privacy</Link>.
          </p>

          <p className="text-center text-xs text-white/55 mt-4">
            Need help?{" "}
            <Link to="/contact" className="text-[#4CAF50] font-semibold hover:underline" data-testid="login-contact-link">
              Contact us
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
